<!-- eslint-disable max-len -->
<template>
  <form
    class="multi-store-modal"
    :class="`multi-store-modal--${template}`"
    :action="action"
    :method="method"
    @submit.prevent="handleLocalizationFormSubmit"
  >
    <!-- Custom close button to bind custom close event -->
    <div
      class=" multi-store-modal-overlay__close-container overlay__close-container"
    >
      <button
        class="multi-store-modal-overlay__close overlay__close icon-button"
        type="button"
        @click.prevent="handleCloseOverlay"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.close')"
        />

        <icon-close-outline />
      </button>
    </div>

    <div class="multi-store-modal__body">
      <input
        name="_method"
        type="hidden"
        value="put"
      >

      <input
        name="return_to"
        type="hidden"
        :value="returnTo"
      >

      <input
        v-if="active?.instance?.url"
        name="no_redirect"
        type="hidden"
        value="true"
      >

      <!-- Suggestions -->
      <div
        v-if="template === 'suggestions'"
        class="multi-store-modal__suggestions"
      >
        <h2
          class="multi-store-modal__suggestions-title text-heading-m"
          v-html="suggestionStrings.title"
        />

        <ul
          class="multi-store-modal__suggestions-items text-body-2xs-regular"
        >
          <li
            class="multi-store-modal__suggestions-item"
          >
            <div class="multi-store-modal__suggestions-item-bullet">
              <icon-check-outline />
            </div>
            <span v-text="suggestionStrings.currency" />
          </li>

          <li
            v-if="suggestedStore.country.freeShippingThreshold"
            class="multi-store-modal__suggestions-item"
          >
            <div class="multi-store-modal__suggestions-item-bullet">
              <icon-check-outline />
            </div>
            <span v-text="suggestionStrings.shipping" />
          </li>

          <li
            class="multi-store-modal__suggestions-item"
          >
            <div class="multi-store-modal__suggestions-item-bullet">
              <icon-check-outline />
            </div>
            <span v-text="suggestionStrings.language" />
          </li>
        </ul>

        <!-- Hidden inputs to set continue values -->
        <input
          name="country_code"
          type="hidden"
          :value="suggestedStore.country.code"
        >

        <input
          name="language_code"
          type="hidden"
          :value="suggestedStore.language.code"
        >
      </div>

      <!-- Options -->
      <div
        v-else
        class="multi-store-modal__options"
      >
        <h2
          class=" multi-store-modal__options-title text-heading-m"
          v-text="$string('general.multi_store.options.title')"
        />

        <multi-store-options
          class="multi-store-modal__options-selects"
          namespace="modal"
          :language-selector="false"
        />
      </div>

      <!-- Footer -->
      <div class="multi-store-modal__footer">
        <btn
          class="multi-store-modal__continue"
          :label="continueButtonString"
          modifiers="primary block center"
          :show-icon="false"
          type="submit"
        />

        <div class="text-center-mobile">
          <btn
            v-if="template !== 'options'"
            class="multi-store-modal__alt-button"
            :label="altButtonString"
            modifiers="text inline"
            :show-icon="false"
            type="button"
            @click.prevent="handleAltButtonClick"
          />
        </div>
      </div>
    </div>

    <!-- Custom overlay to bind custom close event -->
    <teleport to="body">
      <transition name="fade">
        <button
          v-if="isActive"
          class="window-overlay"
          :class="namespace"
          tabindex="-1"
          type="button"
          aria-hidden="true"
          @click.prevent="handleCloseOverlay"
        />
      </transition>
    </teleport>
  </form>
</template>

<script>

/**
 * Vue: Multi-store modal (multi-store-modal)
 * -----------------------------------------------------------------------------
 * Modal to control multi-store settings.
 *
 * @param {Boolean} isActive - Modal is active.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import MultiStoreOptions from '~async/multi-store-options/multi-store-options'

import IconCheckOutline from '~icons/general/check-outline.svg'
import IconCloseOutline from '~icons/directional-navigation/close-outline.svg'

import { multiStoreAction, multiStoreMethod, multiStoreReturnTo, replaceMultiStoreStrings } from '~/helpers/multi-store'

export default {
  name: 'MultiStoreModal',

  components: {
    Btn,
    IconCheckOutline,
    IconCloseOutline,
    MultiStoreOptions,
  },

  props: {
    isActive: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      namespace: 'multiStoreModal',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
      trigger: (state) => state['multi-store'].trigger,
      template: (state) => state['multi-store'].template,
    }),

    /**
     * Compute active value.
     * - This is selected if user has changed select value, or suggested if user
     *   hasn't updated anything yet.
     */
    active() {
      return this.selectedStore.country ? this.selectedStore : this.suggestedStore
    },

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      return multiStoreAction(this.active?.instance)
    },

    /**
     * Compute alt button string.
     * @returns {String}
     */
    altButtonString() {
      return this.template === 'suggestions'
        ? this.$string('general.multi_store.suggestions.change')
        : this.$string('general.multi_store.options.cancel')
    },

    /**
     * Compute continue button string.
     * @returns {String}
     */
    continueButtonString() {
      return this.template === 'suggestions'
        ? this.$string(
          'general.multi_store.suggestions.continue_html',
          { replace: this.replaceStrings() },
        )
        : this.$string('general.multi_store.options.confirm')
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      return this.active?.instance?.url ? '' : multiStoreReturnTo(this.active)
    },

    /**
     * Compute suggestion strings.
     * @returns {Object}
     */
    suggestionStrings() {
      const aClass = {
        target: '<a',
        value: '<a class="text-body-m-underline"',
      }

      const titleString = this.trigger === 'preferences'
        ? 'general.multi_store.suggestions.preferences.title_html'
        : 'general.multi_store.suggestions.title_html'

      return {
        currency: this.$string(
          'general.multi_store.suggestions.currency_html',
          { replace: this.replaceStrings() },
        ).replaceAll(aClass.target, aClass.value),
        language: this.$string(
          'general.multi_store.suggestions.language_html',
          { replace: this.replaceStrings() },
        ).replaceAll(aClass.target, aClass.value),
        shipping: this.$string(
          'general.multi_store.suggestions.shipping_html',
          { replace: this.replaceStrings() },
        ).replaceAll(aClass.target, aClass.value),
        title: this.$string(
          titleString,
          { replace: this.replaceStrings() },
        ).replaceAll(aClass.target, aClass.value),
      }
    },

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    method() {
      return multiStoreMethod(this.active?.instance)
    },
  },

  watch: {

    /**
     * Watch template changing to set selected store back to suggested store.
     */
    template() {
      if (this.template !== 'suggestions') {
        return
      }

      this.setSelectedStore(this.suggestedStore)
    },
  },

  mounted() {
    cnvs.EventBus.emit('overlay:update', 'multiStoreModal')
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
      setTemplate: 'multi-store/setTemplate',
    }),

    /**
     * Replace text shortcodes.
     * @returns {Object}
     */
    replaceStrings() {
      return replaceMultiStoreStrings(this.suggestedStore.country, this.suggestedStore.language)
    },

    /**
     * Handle localization form submit.
     * @param {Object} event - Submit event.
     */
    async handleLocalizationFormSubmit(event) {
      const form = event.target
      const formData = new FormData(form)

      this.setPreference({
        country: formData.get('country_code'),
        language: formData.get('language_code'),
      })

      /**
       * Wait for preferences to be set and action/returnTo computed props to
       * finish calculating.
       */
      await this.$nextTick()
      form.submit()
    },

    /**
     * Handle alt button click.
     */
    async handleAltButtonClick() {
      this.setTemplate(this.template === 'suggestions'
        ? 'options'
        : 'suggestions')

      await this.$nextTick()
      cnvs.EventBus.emit('overlay:update', this.namespace)
    },

    /**
     * Handle close overlay click.
     */
    handleCloseOverlay() {
      const data = {
        country: this.current.country.code,
        language: this.current.language.code,
      }

      sessionStorage.setItem('multi-store-dismissed', JSON.stringify(data))

      this.closeOverlay(this.namespace)
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-modal';
</style>
